import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import {
  MessageSquare, Search, Sparkles, User,
  Loader2, Clock, ChevronLeft, History, Inbox,
} from 'lucide-react';
import { chatService } from '../services/chatService';
import { cn } from '../lib/utils';

// ─── Message Bubble ────────────────────────────────────────────────

const MessageBubble = ({ message }) => {
  const isUser = message.role === 'user';
  const time = message.timestamp
    ? new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';

  return (
    <div className={cn('flex gap-3 items-start', isUser ? 'flex-row-reverse' : 'flex-row')}>
      {/* Avatar */}
      <div className={cn(
        'h-8 w-8 rounded-full flex-shrink-0 flex items-center justify-center text-white',
        isUser ? 'bg-gray-800' : 'bg-gradient-to-br from-cyan-500 to-blue-600',
      )}>
        {isUser
          ? <User className="h-4 w-4" />
          : <Sparkles className="h-4 w-4" />
        }
      </div>

      {/* Bubble */}
      <div className={cn('flex flex-col gap-1 max-w-[72%]', isUser ? 'items-end' : 'items-start')}>
        <span className="text-xs font-medium text-muted-foreground px-1">
          {isUser ? 'You' : 'Niva'}
        </span>
        <div className={cn(
          'rounded-2xl px-4 py-2.5 text-sm leading-relaxed',
          isUser
            ? 'bg-gray-800 text-white rounded-br-sm'
            : 'bg-white text-gray-800 rounded-bl-sm border border-gray-100 shadow-sm',
        )}>
          <p className="whitespace-pre-wrap">{message.content}</p>
        </div>
        {time && (
          <span className="text-[10px] text-muted-foreground px-1">{time}</span>
        )}
      </div>
    </div>
  );
};

// ─── Session List Item ─────────────────────────────────────────────

const SessionItem = ({ session, isSelected, onClick, formatRelativeDate }) => (
  <button
    onClick={onClick}
    className={cn(
      'w-full text-left px-4 py-3.5 border-b last:border-0 transition-colors',
      'hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50',
      isSelected
        ? 'bg-cyan-50 border-l-[3px] border-l-cyan-500 pl-[13px]'
        : 'border-l-[3px] border-l-transparent',
    )}
  >
    <div className="flex items-start justify-between gap-2 mb-1">
      <p className={cn(
        'text-sm font-medium truncate flex-1 leading-snug',
        isSelected ? 'text-cyan-800' : 'text-gray-800',
      )}>
        {session.title || 'Conversation'}
      </p>
      <span className="text-[10px] text-muted-foreground flex-shrink-0 mt-0.5 whitespace-nowrap">
        {formatRelativeDate(session.lastActivity)}
      </span>
    </div>
    <p className="text-xs text-muted-foreground truncate leading-relaxed">
      {session.lastMessage || 'No messages yet'}
    </p>
    <div className="flex items-center gap-1 mt-1.5">
      <Clock className="h-3 w-3 text-muted-foreground/50" />
      <span className="text-[10px] text-muted-foreground/60">
        {session.messageCount ?? 0} message{session.messageCount !== 1 ? 's' : ''}
      </span>
    </div>
  </button>
);

// ─── Main Component ────────────────────────────────────────────────

const Messages = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  // Mobile: 'list' shows left panel, 'chat' shows right panel
  const [mobilePanel, setMobilePanel] = useState('list');

  const transcriptEndRef = useRef(null);

  // Load sessions on mount; auto-select from URL ?session= param
  useEffect(() => {
    const init = async () => {
      try {
        setLoadingSessions(true);
        const res = await chatService.getChatSessions();
        const list = Array.isArray(res) ? res : res?.sessions || [];
        setSessions(list);

        const sessionParam = searchParams.get('session');
        if (sessionParam) {
          const match = list.find(s => s.sessionId === sessionParam);
          if (match) await openSession(match);
        }
      } catch (err) {
        console.error('Failed to load sessions:', err);
      } finally {
        setLoadingSessions(false);
      }
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll transcript to bottom whenever it loads
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript]);

  const openSession = async (session) => {
    setSelectedSession(session);
    setMobilePanel('chat');
    setLoadingTranscript(true);
    setTranscript([]);
    setSearchParams({ session: session.sessionId }, { replace: true });

    try {
      const history = await chatService.getChatHistory(session.sessionId);
      setTranscript(history.messages || []);
    } catch (err) {
      console.error('Failed to load transcript:', err);
    } finally {
      setLoadingTranscript(false);
    }
  };

  const formatRelativeDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    const diffMs = Date.now() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const filteredSessions = sessions.filter(s => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      (s.title || '').toLowerCase().includes(term) ||
      (s.lastMessage || '').toLowerCase().includes(term)
    );
  });

  return (
    // Escape MainLayout padding so the two panels fill the full viewport height
    <div className="flex -m-4 md:-m-6 overflow-hidden" style={{ height: 'calc(100vh - 3.5rem)' }}>

      {/* ── Left Panel: Session List ─────────────────────────────── */}
      <div className={cn(
        'flex flex-col border-r bg-white flex-shrink-0',
        'w-full md:w-80',
        // On mobile show only the active panel
        mobilePanel === 'chat' ? 'hidden md:flex' : 'flex',
      )}>
        {/* Header */}
        <div className="flex flex-col gap-3 px-4 py-4 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-sm flex items-center gap-2">
              <History className="h-4 w-4 text-muted-foreground" />
              Message History
            </h2>
            {sessions.length > 0 && (
              <span className="text-xs text-muted-foreground">
                {sessions.length} conversation{sessions.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 h-8 text-sm bg-gray-50"
            />
          </div>
        </div>

        {/* Session list */}
        <ScrollArea className="flex-1">
          {loadingSessions ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              <Inbox className="h-8 w-8 text-muted-foreground/30 mb-2" />
              <p className="text-sm text-muted-foreground">
                {searchTerm ? 'No conversations match your search.' : 'No conversations yet.'}
              </p>
            </div>
          ) : (
            filteredSessions.map((session) => (
              <SessionItem
                key={session.sessionId}
                session={session}
                isSelected={selectedSession?.sessionId === session.sessionId}
                onClick={() => openSession(session)}
                formatRelativeDate={formatRelativeDate}
              />
            ))
          )}
        </ScrollArea>
      </div>

      {/* ── Right Panel: Transcript ──────────────────────────────── */}
      <div className={cn(
        'flex-1 flex flex-col bg-gray-50/40 min-w-0',
        mobilePanel === 'list' ? 'hidden md:flex' : 'flex',
      )}>

        {/* Mobile: back button */}
        <div className="md:hidden flex items-center px-3 py-2 border-b bg-white flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobilePanel('list')}
            className="gap-1.5 text-muted-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
            All Conversations
          </Button>
        </div>

        {!selectedSession ? (
          /* Empty state */
          <div className="flex flex-col items-center justify-center flex-1 text-center px-6">
            <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-cyan-100 to-blue-100 flex items-center justify-center mb-4 shadow-sm">
              <Sparkles className="h-8 w-8 text-cyan-500" />
            </div>
            <h3 className="font-semibold text-gray-700 mb-1">Select a conversation</h3>
            <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">
              Choose a conversation from the list on the left to view the full transcript.
            </p>
          </div>
        ) : (
          <>
            {/* Transcript header */}
            <div className="flex-shrink-0 px-6 py-4 border-b bg-white shadow-sm">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-4 w-4 text-white" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate text-sm">
                    {selectedSession.title || 'Conversation'}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {selectedSession.messageCount ?? 0} messages
                    {selectedSession.lastActivity && (
                      <> &middot; Last active {formatRelativeDate(selectedSession.lastActivity)}</>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 px-4 md:px-8 py-6">
              {loadingTranscript ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : transcript.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <MessageSquare className="h-8 w-8 text-muted-foreground/30 mb-2" />
                  <p className="text-sm text-muted-foreground">No messages in this conversation.</p>
                </div>
              ) : (
                <div className="flex flex-col gap-5 max-w-3xl mx-auto">
                  {transcript.map((msg, i) => {
                    // Group separator: show date divider when the date changes
                    const showDateDivider = i === 0 || (
                      msg.timestamp &&
                      transcript[i - 1]?.timestamp &&
                      new Date(msg.timestamp).toDateString() !==
                        new Date(transcript[i - 1].timestamp).toDateString()
                    );

                    return (
                      <React.Fragment key={i}>
                        {showDateDivider && msg.timestamp && (
                          <div className="flex items-center gap-3 my-2">
                            <div className="flex-1 h-px bg-gray-200" />
                            <span className="text-[10px] text-muted-foreground font-medium whitespace-nowrap">
                              {new Date(msg.timestamp).toLocaleDateString([], {
                                weekday: 'long', month: 'long', day: 'numeric',
                              })}
                            </span>
                            <div className="flex-1 h-px bg-gray-200" />
                          </div>
                        )}
                        <MessageBubble message={msg} />
                      </React.Fragment>
                    );
                  })}
                  <div ref={transcriptEndRef} />
                </div>
              )}
            </ScrollArea>
          </>
        )}
      </div>
    </div>
  );
};

export default Messages;
